import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { 
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Loader2,
  Volume2,
  VolumeX,
  Mail,
  Share2,
  PlayCircle,
  PauseCircle,
  Check,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LanguagePhrasesPage() {
  const { languageCode } = useParams();
  const { t } = useLanguage();
  const [languageData, setLanguageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [playingPhrase, setPlayingPhrase] = useState(null);
  const [isPlayingAll, setIsPlayingAll] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const audioRef = useRef(null);
  const playQueueRef = useRef([]);

  useEffect(() => {
    fetchLanguageData();
  }, [languageCode]);

  const fetchLanguageData = async () => {
    try {
      const response = await api.get(`/phrases/${languageCode}`);
      const data = response.data;
      setLanguageData(data);
      // Expand all categories by default
      const expanded = {};
      Object.keys(data.categories).forEach(key => {
        expanded[key] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      console.error('Failed to load language data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const playAudio = async (phrase) => {
    try {
      if (playingPhrase === phrase.id) {
        // Stop current audio
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
        setPlayingPhrase(null);
        return;
      }

      // Stop any existing audio
      if (audioRef.current) {
        audioRef.current.pause();
      }

      setPlayingPhrase(phrase.id);
      
      const audio = new Audio(`${API_URL}${phrase.audio_url}`);
      audioRef.current = audio;
      
      audio.onended = () => {
        setPlayingPhrase(null);
        if (isPlayingAll && playQueueRef.current.length > 0) {
          const nextPhrase = playQueueRef.current.shift();
          setTimeout(() => playAudio(nextPhrase), 500);
        } else {
          setIsPlayingAll(false);
        }
      };
      
      audio.onerror = () => {
        toast.error('Audio unavailable — voice synthesis may not be configured on this server');
        setPlayingPhrase(null);
      };

      await audio.play();
    } catch (error) {
      console.error('Audio playback error:', error);
      toast.error('Audio playback failed — voice synthesis requires OPENAI_API_KEY on the server');
      setPlayingPhrase(null);
    }
  };

  const playAllAudio = () => {
    if (isPlayingAll) {
      // Stop playing
      if (audioRef.current) {
        audioRef.current.pause();
      }
      playQueueRef.current = [];
      setIsPlayingAll(false);
      setPlayingPhrase(null);
      return;
    }

    // Build queue of all phrases
    const allPhrases = [];
    Object.values(languageData.categories).forEach(category => {
      allPhrases.push(...category.phrases);
    });
    
    playQueueRef.current = allPhrases.slice(1);
    setIsPlayingAll(true);
    
    if (allPhrases.length > 0) {
      playAudio(allPhrases[0]);
    }
  };

  const emailPhrases = async () => {
    const email = prompt('Enter your email address:');
    if (!email) return;

    try {
      await api.post('/phrases/email', {
        email,
        language_code: languageCode
      });
      
      setEmailSent(true);
      toast.success(`Phrases sent to ${email}`);
      setTimeout(() => setEmailSent(false), 3000);
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  const shareLink = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Essential ${languageData?.name} Phrases - TLA Housing Europe`,
          text: 'Learn essential phrases for daily life in Europe',
          url
        });
      } catch (error) {
        // User cancelled or error
      }
    } else {
      // Fallback to clipboard
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setLinkCopied(false), 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
      </div>
    );
  }

  if (!languageData) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20">
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-4">{t('phrases.not_found', 'Language not found')}</h1>
          <Link to="/phrases" className="text-signal-blue hover:underline">
            {t('phrases.back_to_languages', '← Back to languages')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="language-phrases-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/phrases" 
            className="inline-flex items-center text-sm text-slate-600 hover:text-signal-blue mb-4"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            All Languages
          </Link>
          
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-4xl">{languageData.flag}</span>
                <h1 className="font-heading text-3xl font-bold text-slate-800">
                  Essential {languageData.name} Phrases
                </h1>
              </div>
              <p className="text-slate-600">
                Practical expressions for work, daily life, and getting around.
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={emailPhrases}
                className="border-slate-300 text-slate-700"
                data-testid="email-phrases-btn"
              >
                {emailSent ? <Check className="w-4 h-4 mr-1 text-green-500" /> : <Mail className="w-4 h-4 mr-1" />}
                Email to myself
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={shareLink}
                className="border-slate-300 text-slate-700"
                data-testid="share-link-btn"
              >
                {linkCopied ? <Check className="w-4 h-4 mr-1 text-green-500" /> : <Share2 className="w-4 h-4 mr-1" />}
                Share link
              </Button>
              <Button
                size="sm"
                onClick={playAllAudio}
                className={isPlayingAll ? 'bg-red-500 hover:bg-red-600' : 'bg-signal-blue hover:bg-signal-blue-hover'}
                data-testid="play-all-btn"
              >
                {isPlayingAll ? (
                  <>
                    <PauseCircle className="w-4 h-4 mr-1" />
                    Stop
                  </>
                ) : (
                  <>
                    <PlayCircle className="w-4 h-4 mr-1" />
                    Play all
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="space-y-4">
          {Object.entries(languageData.categories).map(([categoryKey, category]) => (
            <div 
              key={categoryKey} 
              className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
              data-testid={`category-${categoryKey}`}
            >
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(categoryKey)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
              >
                <h2 className="font-heading text-lg font-semibold text-slate-800">
                  {category.title}
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">{category.phrases.length} phrases</span>
                  {expandedCategories[categoryKey] ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </button>

              {/* Phrases */}
              {expandedCategories[categoryKey] && (
                <div className="border-t border-slate-200">
                  {category.phrases.map((phrase, index) => (
                    <div 
                      key={phrase.id}
                      className={`flex items-start gap-4 p-4 ${
                        index !== category.phrases.length - 1 ? 'border-b border-slate-100' : ''
                      } ${playingPhrase === phrase.id ? 'bg-signal-blue/5' : 'hover:bg-slate-50'} transition-colors`}
                      data-testid={`phrase-${phrase.id}`}
                    >
                      {/* Audio Button */}
                      <button
                        onClick={() => playAudio(phrase)}
                        className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                          playingPhrase === phrase.id 
                            ? 'bg-signal-blue text-white' 
                            : 'bg-slate-100 text-slate-600 hover:bg-signal-blue/10 hover:text-signal-blue'
                        }`}
                        data-testid={`play-${phrase.id}`}
                      >
                        {playingPhrase === phrase.id ? (
                          <Volume2 className="w-5 h-5 animate-pulse" />
                        ) : (
                          <Volume2 className="w-5 h-5" />
                        )}
                      </button>

                      {/* Phrase Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-lg font-semibold text-slate-800 mb-1">
                          {phrase.native}
                        </p>
                        <p className="text-sm text-slate-500 mb-1">
                          ({phrase.phonetic})
                        </p>
                        <p className="text-slate-600">
                          {phrase.english}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-slate-500 text-sm">
            Simple. Practical. Human. — Built to help you feel confident from day one.
          </p>
        </div>
      </div>
    </div>
  );
}
